import React from "react";
import { motion } from "framer-motion";
import {
  Globe,
  Smartphone,
  Bot,
  CreditCard,
  LineChart,
  ShieldCheck,
  Layers,
  ArrowRight,
} from "lucide-react";

const SERVICES = [
  { id: "website",  Icon: Globe,      label: "Website",      caption: "Your storefront, beautifully designed",  color: "#60a5fa" },
  { id: "app",      Icon: Smartphone, label: "Mobile App",   caption: "Brand in your customer's pocket",        color: "#a78bfa" },
  { id: "ai",       Icon: Bot,        label: "AI Assistant", caption: "24/7 chat in Hindi + English",           color: "#ec4899" },
  { id: "payments", Icon: CreditCard, label: "Payments",     caption: "UPI · Cards · Wallets — all settled",    color: "#22d3ee" },
  { id: "analytics",Icon: LineChart,  label: "Analytics",    caption: "Live revenue & customer dashboard",      color: "#34d399" },
];

export default function Features() {
  return (
    <section
      id="services"
      data-testid="features-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[60%] radial-glow-violet blur-3xl opacity-40" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <p className="uppercase text-xs tracking-[0.24em] text-violet-300 font-semibold flex items-center justify-center gap-2">
            <Layers className="w-3.5 h-3.5 text-pink-300" />
            One unified platform
          </p>
          <h2
            data-testid="features-heading"
            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
          >
            Everything you need to{" "}
            <span className="text-gradient">run your business online.</span>
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">
            Website, mobile app, AI assistant, payments and analytics — all in
            one place, beautifully connected.
          </p>
        </div>

        {/* Clean horizontal flow */}
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`feature-card-${i}`}
              className="group relative rounded-2xl glass border border-white/5 p-5 sm:p-6 hover:-translate-y-1 transition-all"
              style={{ "--c": s.color }}
            >
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                style={{
                  background: `radial-gradient(circle at 20% 0%, ${s.color}22 0%, transparent 65%)`,
                  border: `1px solid ${s.color}66`,
                }}
              />
              <div className="relative">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{
                    background: `linear-gradient(135deg, ${s.color}33, transparent)`,
                    border: `1px solid ${s.color}66`,
                  }}
                >
                  <s.Icon className="w-5 h-5" style={{ color: s.color }} strokeWidth={1.8} />
                </div>
                <p className="font-display text-base sm:text-lg font-bold text-white">{s.label}</p>
                <p className="mt-1.5 text-xs sm:text-sm text-slate-400 leading-relaxed">
                  {s.caption}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Connecting flow line (desktop only) */}
        <div className="hidden lg:flex items-center justify-center mt-10 gap-3 text-xs uppercase tracking-[0.24em] text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
          <span>All connected · One dashboard · Secure & compliant</span>
          <ArrowRight className="w-3.5 h-3.5 text-violet-300" />
        </div>
        <div className="lg:hidden flex items-center justify-center mt-8 gap-2 text-xs uppercase tracking-widest text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
          <span>One dashboard · Secure</span>
        </div>
      </div>
    </section>
  );
}
